import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { AppointmentItem } from '@crm/shared';
import { useAuth } from '../auth/AuthContext';
import { useUserDirectory } from '../schedules/api';
import { useOverdueAppointments } from './api';
import { AppointmentModal, type ModalInitial } from './AppointmentModal';

/**
 * Faza 19 — strona „Do zatwierdzenia". Lista zaległych wizyt
 * (BOOKED/CONFIRMED z `startAt < now`) z możliwością otwarcia modala
 * wizyty i wykonania akcji terminalnej (Zakończ / Nie przyszedł / Anuluj).
 *
 * Zakres widoczności pilnowany przez backend:
 *   - EMPLOYEE: tylko własne (parametr employeeId ignorowany serwerowo),
 *   - RECEPTION/ADMIN: wszystkie, z opcjonalnym filtrem po pracowniku.
 *
 * Wiersze wizyt starszych niż próg OVERDUE_COMPLETION_DAYS (30 dni) są
 * podświetlane na czerwono z podpisem „Tylko admin". Threshold jest
 * tylko hintem UI; backend i tak zwróci 409 dla nie-ADMIN-a.
 */
const OVERDUE_THRESHOLD_DAYS = 30;

function daysSince(iso: string): number {
  const ms = Date.now() - new Date(iso).getTime();
  return Math.floor(ms / 86_400_000);
}

function formatDateTime(iso: string): string {
  return new Intl.DateTimeFormat('pl-PL', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

export function OverduePage(): ReactNode {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isEmployeeRole = user?.role === 'EMPLOYEE';

  const [filterEmployeeId, setFilterEmployeeId] = useState<number | null>(null);
  const [modalInitial, setModalInitial] = useState<ModalInitial | null>(null);

  const directoryQuery = useUserDirectory();
  const employees = useMemo(() => {
    return (directoryQuery.data ?? []).filter((u) => u.bookable);
  }, [directoryQuery.data]);

  const overdueQuery = useOverdueAppointments(
    isEmployeeRole ? undefined : filterEmployeeId ?? undefined,
  );

  const items = overdueQuery.data ?? [];

  function handleClick(item: AppointmentItem): void {
    setModalInitial({ mode: 'edit', appointment: item });
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-zinc-900">
          {t('appointments.overdue.title')}
        </h1>
        <p className="text-sm text-zinc-500">{t('appointments.overdue.subtitle')}</p>
      </div>

      {!isEmployeeRole && employees.length > 0 && (
        <div className="flex items-center gap-3 rounded-lg bg-white p-3 shadow-sm">
          <label className="text-sm text-zinc-700">
            {t('appointments.overdue.filterEmployee')}
          </label>
          <select
            value={filterEmployeeId ?? ''}
            onChange={(e) =>
              setFilterEmployeeId(e.target.value === '' ? null : Number(e.target.value))
            }
            className="input max-w-xs"
          >
            <option value="">{t('appointments.allEmployees')}</option>
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.firstName} {emp.lastName}
              </option>
            ))}
          </select>
          {overdueQuery.isFetching && (
            <span className="text-xs text-zinc-500">{t('common.loading')}</span>
          )}
        </div>
      )}

      {items.length === 0 ? (
        <div className="rounded-lg bg-white p-8 text-center text-sm text-zinc-500 shadow-sm">
          {overdueQuery.isLoading ? t('common.loading') : t('appointments.overdue.empty')}
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg bg-white shadow-sm">
          <table className="min-w-full divide-y divide-zinc-200 text-sm">
            <thead className="bg-zinc-50">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-zinc-700">
                  {t('appointments.overdue.col.dateTime')}
                </th>
                <th className="px-4 py-2 text-left font-medium text-zinc-700">
                  {t('appointments.overdue.col.age')}
                </th>
                <th className="px-4 py-2 text-left font-medium text-zinc-700">
                  {t('appointments.overdue.col.client')}
                </th>
                {!isEmployeeRole && (
                  <th className="px-4 py-2 text-left font-medium text-zinc-700">
                    {t('appointments.overdue.col.employee')}
                  </th>
                )}
                <th className="px-4 py-2 text-left font-medium text-zinc-700">
                  {t('appointments.overdue.col.services')}
                </th>
                <th className="px-4 py-2 text-right font-medium text-zinc-700">
                  {t('appointments.overdue.col.price')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {items.map((apt) => {
                const days = daysSince(apt.startAt);
                const isOldOverdue = days > OVERDUE_THRESHOLD_DAYS;
                return (
                  <tr
                    key={apt.id}
                    onClick={() => handleClick(apt)}
                    className={`cursor-pointer transition ${
                      isOldOverdue
                        ? 'border-l-4 border-l-red-400 bg-red-50/40 hover:bg-red-50'
                        : 'hover:bg-zinc-50'
                    }`}
                  >
                    <td className="px-4 py-2 text-zinc-900">
                      {formatDateTime(apt.startAt)}
                    </td>
                    <td className="px-4 py-2">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                          isOldOverdue
                            ? 'bg-red-100 text-red-700'
                            : 'bg-amber-100 text-amber-800'
                        }`}
                      >
                        {t('appointments.overdue.daysAgo', { count: days })}
                      </span>
                      {isOldOverdue && (
                        <span className="ml-2 text-xs text-red-700">
                          {t('appointments.overdue.adminOnlyHint')}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-zinc-900">
                      {apt.client.firstName} {apt.client.lastName ?? ''}
                    </td>
                    {!isEmployeeRole && (
                      <td className="px-4 py-2">
                        <span
                          className="inline-flex items-center gap-1.5 text-zinc-700"
                        >
                          <span
                            className="inline-block h-2 w-2 rounded-full"
                            style={{ background: apt.employee.color ?? '#a1a1aa' }}
                          />
                          {apt.employee.firstName} {apt.employee.lastName}
                        </span>
                      </td>
                    )}
                    <td className="px-4 py-2 text-zinc-700">
                      {apt.services.map((s) => s.name).join(', ')}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums text-zinc-900">
                      {apt.totalPrice} zł
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modalInitial && (
        <AppointmentModal
          initial={modalInitial}
          onClose={() => setModalInitial(null)}
          employees={employees}
        />
      )}
    </div>
  );
}
